import { lazy } from "react";
import { Navigate, createBrowserRouter } from "react-router-dom";

const Layouts = lazy(() => import("../layouts/index"));
const Documents = lazy(() => import("../pages/documents"));
const RecordMetadata = lazy(() => import("../pages/recordMetadata"));
const EditCommitment = lazy(() => import("../pages/commitments/EditCommitment"));
const LinkModuleRecord = lazy(
  () => import("../pages/components/LinkModuleRecord"),
);
const LocalChangeDocuments = lazy(
  () => import("../pages/localChanges/LocalChangeDocuments"),
);
const ImpactAssessment = lazy(
  () => import("../pages/localChanges/ImpactAssessment"),
);

const LcmList = lazy(() => import("../pages/lcm"));
const ObligationsList = lazy(() => import("../pages/obligations"));
const CommitmentsList = lazy(() => import("../pages/commitments"));
const LocalChangesList = lazy(() => import("../pages/localChanges"));
const LocalChangesWorkflow = lazy(
  () => import("../pages/localChanges/LocalChangesWorkflow"),
);
const AddObligation = lazy(() => import("../pages/obligations/AddObligation"));
const EditObligation = lazy(
  () => import("../pages/obligations/EditObligation"),
);
const AddCommitment = lazy(() => import("../pages/commitments/AddCommitment"));

const SubModulePage = lazy(() => import("../pages/dynamic/SubModulePage"));
const DynamicViewPage = lazy(() => import("../pages/dynamic/ViewPage"));
const DynamicEditPage = lazy(() => import("../pages/dynamic/EditPage"));
const AddLocalChange = lazy(
  () => import("../pages/localChanges/AddLocalChange"),
);

const EditLocalChanges = lazy(
  () => import("../pages/localChanges/EditLocalChange"),
);

const EditRecordMetadata = lazy(
  () => import("../pages/recordMetadata/EditRecordMetadata"),
);

const RegistrationUpdate = lazy(
  () => import("../pages/localChanges/registration-update/Index"),
);

const RegistrationUpdateDetail = lazy(
  () => import("../pages/localChanges/RegistrationUpdateDetail"),
);

const GlobalSearchList = lazy(() => import("../pages/globalSearch/index"));

const AssociateProcessPage = lazy(
  () => import("../pages/workflow/AssociateProcessPage"),
);
const InitiateProcessPage = lazy(
  () => import("../pages/workflow/InitiateProcessPage"),
);
const ImpactedRecord = lazy(() => import("../pages/impactedRecords/index"));
const TriggeringRecords = lazy(() => import("../pages/triggeringRecords/index"));
const MenuLayout = lazy(() => import("../components/MenuLayout"));

const routesForm = [
  {
    path: "/*",
    element: <Navigate to="/lcm" />,
  },
  {
    path: "/global-search-list",
    element: <Layouts />,
    children: [
      {
        path: "/global-search-list",
        element: <GlobalSearchList />,
      },
    ],
  },
  {
    path: "/lcm",
    element: <Layouts />,
    children: [
      {
        path: "",
        element: <LcmList />,
      },
      {
        path: "associate-process/:lcmType/:lcmId/:recordId",
        element: <AssociateProcessPage />,
        handle: {
          hideBottomBar: true,
        },
      },
      {
        path: "initiate-process/:lcmType/:lcmId/:processAssociationId/:recordId",
        element: <InitiateProcessPage />,
        handle: {
          hideBottomBar: true,
        },
      },
    ],
  },
  {
    path: "/obligations",
    element: <Layouts />,
    handle: {
      moduleType: "obligations",
    },
    children: [
      {
        path: "",
        element: <ObligationsList />,
      },
      {
        path: "add-obligation",
        element: <AddObligation />,
      },
      {
        path: "edit-obligation/:lcmId",
        children: [
          {
            path: "obligation-information",
            element: <EditObligation />,
            handle: {
              breadcrumbLevel: "basicInfo",
            },
          },
          {
            path: "documents-list",
            element: <Documents viewMode={false} />,
          },
          {
            path: "linked-records",
            element: <LinkModuleRecord />,
          },
          {
            path: "impacted-records",
            element: <ImpactedRecord />,
          },
          {
            path: "triggering-records",
            element: <TriggeringRecords />,
          },
          {
            path: "record-metadata",
            element: <MenuLayout />,
            children: [
              {
                index: true,
                element: <RecordMetadata viewMode={false} />,
              },
              {
                path: "addmetadata/:docId",
                element: <EditRecordMetadata />,
                handle: {
                  breadcrumbLevel: "add",
                },
              },
              {
                path: "editmetadata/:docId/:id",
                element: <EditRecordMetadata />,
                handle: {
                  breadcrumbLevel: "edit",
                },
              },
            ],
          },
          {
            path: ":menuName",
            element: <MenuLayout />,
            children: [
              {
                index: true,
                element: <SubModulePage viewMode={false} />,
              },
              {
                path: "add",
                element: <DynamicEditPage />,
                handle: {
                  breadcrumbLevel: "add",
                },
              },
              {
                path: "edit/:id",
                element: <DynamicEditPage />,
                handle: {
                  breadcrumbLevel: "edit",
                },
              },
              {
                path: "view/:id",
                element: <DynamicViewPage />,
                handle: {
                  breadcrumbLevel: "view",
                },
              },
            ],
          },
        ],
      },
      {
        path: "view-obligation/:lcmId",
        children: [
          {
            path: "obligation-information",
            element: <EditObligation />,
            handle: {
              moduleType: "obligations",
              breadcrumbLevel: "basicInfo",
            },
          },
          {
            path: "linked-records",
            element: <LinkModuleRecord />,
          },
          {
            path: "impacted-records",
            element: <ImpactedRecord />,
          },
          {
            path: "triggering-records",
            element: <TriggeringRecords />,
          },
          {
            path: "documents-list",
            element: <Documents viewMode={true} />,
          },
          {
            path: "documents",
            element: <Documents viewMode={true} />,
          },
          {
            path: "record-metadata",
            element: <MenuLayout />,
            children: [
              {
                index: true,
                element: <RecordMetadata viewMode={true} />,
              },
              {
                path: "viewmetadata/:docId/:id",
                element: <EditRecordMetadata />,
                handle: {
                  breadcrumbLevel: "view",
                },
              },
            ],
          },
          {
            path: ":menuName",
            element: <MenuLayout />,
            children: [
              {
                index: true,
                element: <SubModulePage viewMode={true} />,
              },
              {
                path: "add",
                element: <DynamicEditPage />,
                handle: {
                  breadcrumbLevel: "add",
                },
              },
              {
                path: "edit/:id",
                element: <DynamicEditPage />,
                handle: {
                  breadcrumbLevel: "edit",
                },
              },
              {
                path: "view/:id",
                element: <DynamicViewPage />,
                handle: {
                  breadcrumbLevel: "view",
                },
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "/commitments",
    element: <Layouts />,
    handle: {
      moduleType: "commitments",
    },
    children: [
      {
        path: "",
        element: <CommitmentsList />,
      },
      {
        path: "add-commitment",
        element: <AddCommitment />,
      },
      {
        path: "edit-commitment/:lcmId",
        children: [
          {
            path: "commitment-information",
            element: <EditCommitment />,
            handle: {
              breadcrumbLevel: "basicInfo",
            },
          },
          {
            path: "documents-list",
            element: <Documents viewMode={false} />,
          },
          {
            path: "documents",
            element: <Documents viewMode={false} />,
          },
          {
            path: "linked-records",
            element: <LinkModuleRecord />,
          },
          {
            path: "linked-records",
            element: <LinkModuleRecord />,
          },
          {
            path: "impacted-records",
            element: <ImpactedRecord />,
          },
          {
            path: "triggering-records",
            element: <TriggeringRecords />,
          },
          {
            path: "record-metadata",
            element: <MenuLayout />,
            children: [
              {
                index: true,
                element: <RecordMetadata viewMode={false} />,
              },
              {
                path: "addmetadata/:docId",
                element: <EditRecordMetadata />,
                handle: {
                  breadcrumbLevel: "add",
                },
              },
              {
                path: "editmetadata/:docId/:id",
                element: <EditRecordMetadata />,
                handle: {
                  breadcrumbLevel: "edit",
                },
              },
            ],
          },
          {
            path: ":menuName",
            element: <MenuLayout />,
            children: [
              {
                index: true,
                element: <SubModulePage viewMode={false} />,
              },
              {
                path: "add",
                element: <DynamicEditPage />,
                handle: {
                  breadcrumbLevel: "add",
                },
              },
              {
                path: "edit/:id",
                element: <DynamicEditPage />,
                handle: {
                  breadcrumbLevel: "edit",
                },
              },
              {
                path: "view/:id",
                element: <DynamicViewPage />,
                handle: {
                  breadcrumbLevel: "view",
                },
              },
            ],
          },
        ],
      },
      {
        path: "view-commitment/:lcmId",
        children: [
          {
            path: "commitment-information",
            element: <EditCommitment />,
            handle: {
              breadcrumbLevel: "basicInfo",
            },
          },
          {
            path: "documents-list",
            element: <Documents viewMode={true} />,
          },
          {
            path: "impacted-records",
            element: <ImpactedRecord />,
          },
          {
            path: "triggering-records",
            element: <TriggeringRecords />,
          },
          {
            path: "record-metadata",
            element: <MenuLayout />,
            children: [
              {
                index: true,
                element: <RecordMetadata viewMode={true} />,
              },
              {
                path: "viewmetadata/:docId/:id",
                element: <EditRecordMetadata />,
                handle: {
                  breadcrumbLevel: "view",
                },
              },
            ],
          },
          {
            path: ":menuName",
            element: <MenuLayout />,
            children: [
              {
                index: true,
                element: <SubModulePage viewMode={true} />,
              },
              {
                path: "add",
                element: <DynamicEditPage />,
                handle: {
                  breadcrumbLevel: "add",
                },
              },
              {
                path: "edit/:id",
                element: <DynamicEditPage />,
                handle: {
                  breadcrumbLevel: "edit",
                },
              },
              {
                path: "view/:id",
                element: <DynamicViewPage />,
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "/local-updates",
    element: <Layouts />,
    handle: {
      moduleType: "local-updates",
    },
    children: [
      {
        path: "",
        element: <LocalChangesList />,
      },
      {
        path: "add-local-update",
        element: <AddLocalChange />,
      },
      {
        path: "local-update-workflow",
        element: <LocalChangesWorkflow />,
      },
      {
        path: "edit-local-update/:lcmId",
        children: [
          {
            path: "local-update-information",
            element: <EditLocalChanges />,
            handle: {
              breadcrumbLevel: "basicInfo",
            },
          },
          {
            path: "documents-list",
            element: <Documents viewMode={false} />,
          },
          {
            path: "documents",
            element: <LocalChangeDocuments />,
          },
          {
            path: "linked-records",
            element: <LinkModuleRecord />,
          },
          {
            path: "impact-assessment",
            element: <ImpactAssessment />,
          },
          {
            path: "impacted-records",
            element: <ImpactedRecord />,
          },
          {
            path: "triggering-records",
            element: <TriggeringRecords />,
          },
          {
            path: ":menuName",
            element: <MenuLayout />,
            children: [
              {
                index: true,
                element: <SubModulePage viewMode={false} />,
              },
              {
                path: "add",
                element: <DynamicEditPage />,
                handle: {
                  breadcrumbLevel: "add",
                },
              },
              {
                path: "view/:id",
                element: <DynamicViewPage />,
                handle: {
                  breadcrumbLevel: "view",
                },
              },
              {
                path: "edit/:id",
                element: <DynamicEditPage />,
                handle: {
                  breadcrumbLevel: "edit",
                },
              },
            ],
          },
          {
            path: "record-metadata",
            element: <MenuLayout />,
            children: [
              {
                index: true,
                element: <RecordMetadata viewMode={false} />,
              },
              {
                path: "addmetadata/:docId",
                element: <EditRecordMetadata />,
                handle: {
                  breadcrumbLevel: "add",
                },
              },
              {
                path: "editmetadata/:docId/:id",
                element: <EditRecordMetadata />,
                handle: {
                  breadcrumbLevel: "edit",
                },
              },
            ],
          },
          {
            path: "registration-update",
            element: <RegistrationUpdate />,
          },
          {
            path: "registration-update-detail/:relatedId/:recordId",
            element: <RegistrationUpdateDetail />,
            handle: {
              breadcrumbLevel: "registrationDetail",
            },
          },
          {
            path: "registration-update-detail-view/:relatedId/:recordId",
            element: <RegistrationUpdateDetail />,
            handle: {
              breadcrumbLevel: "registrationDetail",
            },
          },
        ],
      },
      {
        path: "view-local-update/:lcmId",
        children: [
          {
            path: "local-update-information",
            element: <EditLocalChanges />,
            handle: {
              breadcrumbLevel: "basicInfo",
            },
          },
          {
            path: "documents-list",
            element: <Documents viewMode={true} />,
          },
          {
            path: "documents",
            element: <LocalChangeDocuments />,
          },
          {
            path: "linked-records",
            element: <LinkModuleRecord />,
          },
          {
            path: "impact-assessment",
            element: <ImpactAssessment />,
          },
          {
            path: "impacted-records",
            element: <ImpactedRecord />,
          },
          {
            path: "triggering-records",
            element: <TriggeringRecords />,
          },
          {
            path: ":menuName",
            element: <MenuLayout />,
            children: [
              {
                index: true,
                element: <SubModulePage viewMode={true} />,
              },
              {
                path: "add",
                element: <DynamicEditPage />,
                handle: {
                  breadcrumbLevel: "add",
                },
              },
              {
                path: "edit/:id",
                element: <DynamicEditPage />,
                handle: {
                  breadcrumbLevel: "edit",
                },
              },
              {
                path: "view/:id",
                element: <DynamicViewPage />,
                handle: {
                  breadcrumbLevel: "view",
                },
              },
            ],
          },
          {
            path: "record-metadata",
            element: <MenuLayout />,
            children: [
              {
                index: true,
                element: <RecordMetadata viewMode={true} />,
              },
              {
                path: "viewmetadata/:docId/:id",
                element: <EditRecordMetadata />,
                handle: {
                  breadcrumbLevel: "view",
                },
              },
            ],
          },
          {
            path: "registration-update",
            element: <RegistrationUpdate />,
          },
          {
            path: "registration-update-detail/:relatedId/:recordId",
            element: <RegistrationUpdateDetail />,
            handle: {
              breadcrumbLevel: "registrationDetail",
            },
          },
          {
            path: "registration-update-detail-view/:relatedId/:recordId",
            element: <RegistrationUpdateDetail />,
            handle: {
              breadcrumbLevel: "registrationDetail",
            },
          },
        ],
      },
    ],
  },
];
const Router = createBrowserRouter(routesForm);

export { Router };
